import {
    Box,
    Button,
    FormLabel,
    HStack,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    SimpleGrid,
    Switch,
    Text,
    useColorModeValue,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useGetWhatsAppConfig } from "../hooks/useGetWhatsAppConfig";
import { useGetWhapiGroups } from "../hooks/useGetWhapiGroups";
import { useUpdateWhatsAppConfig } from "../hooks/useUpdateWhatsAppConfig";
import ScopePrompt from "../../_shared/ScopePrompt";
import { classifyRead } from "../../_shared/readState";

export default function WhatsAppConfigManager() {
    const { selectedOrganization } = useSelectedOrganization();
    const toast = useToast();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');

    const orgId = selectedOrganization?.id || '';
    // Converted at run 124 — the run-104 shape, NOT a live spinner. The
    // `if (!selectedOrganization) return null` below completely re-derives this
    // read's `!!organizationId` gate, so the never-issued state is unreachable
    // TODAY; the conversion kills that store-derived dependency (D62), because
    // the guard and the gate are two statements nothing keeps in step, and the
    // raw `isLoading` this replaces is `true` for a disabled query in React
    // Query v4 — i.e. a permanent "Loading…" the moment the guard drifts.
    const { data: config, isError, isInitialLoading } = useGetWhatsAppConfig(orgId);
    const configReadState = classifyRead(isError, isInitialLoading, config);

    const { mutateAsync: updateConfig, isLoading: isSaving } = useUpdateWhatsAppConfig(orgId, () => {
        toast({ title: translate('organization.whatsapp_config_saved'), status: 'success', duration: 2000 });
    });

    const [enabled, setEnabled] = useState(false);
    const [phoneNumberId, setPhoneNumberId] = useState('');
    const [businessAccountId, setBusinessAccountId] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [tokenPlaceholder, setTokenPlaceholder] = useState('');
    const [tokenEdited, setTokenEdited] = useState(false);
    const [replacementNotifyPhone, setReplacementNotifyPhone] = useState('');
    const [whapiApiToken, setWhapiApiToken] = useState('');
    const [whapiTokenPlaceholder, setWhapiTokenPlaceholder] = useState('');
    const [whapiTokenEdited, setWhapiTokenEdited] = useState(false);
    const [whapiGroupId, setWhapiGroupId] = useState('');
    const [loadGroups, setLoadGroups] = useState(false);

    const hasWhapiToken = config?.hasWhapiApiToken || whapiTokenEdited;
    // Newly VISIBLE at run 124, not newly written: `useGetWhapiGroups` gates on
    // `!!organizationId && enabled` and its signature carries a parameter called
    // `enabled`, so until this run the census captured the type annotation
    // (`'boolean) => {'`) as the gate and the hook sat outside the scoped
    // population with this call site unexamined. The behaviour was already
    // right — a refusal says so below, and the never-issued arm is the manual
    // group-id input beside a "Load groups" button — so this is a conversion
    // that makes the read carry its own evidence, not a fix.
    const {
        data: whapiGroups,
        isFetching: isLoadingGroups,
        isError: isGroupsError,
        isInitialLoading: isGroupsInitialLoading,
    } = useGetWhapiGroups(orgId, loadGroups && !!hasWhapiToken);
    const groupsReadState = classifyRead(isGroupsError, isGroupsInitialLoading, whapiGroups);

    useEffect(() => {
        if (config) {
            setEnabled(config.whatsappEnabled);
            setPhoneNumberId(config.whatsappPhoneNumberId || '');
            setBusinessAccountId(config.whatsappBusinessAccountId || '');
            setAccessToken('');
            setTokenEdited(false);
            setTokenPlaceholder(config.hasAccessToken ? config.accessTokenLastChars || '****' : '');
            setReplacementNotifyPhone(config.whatsappReplacementNotifyPhone || '');
            setWhapiApiToken('');
            setWhapiTokenEdited(false);
            setWhapiTokenPlaceholder(config.hasWhapiApiToken ? config.whapiApiTokenLastChars || '****' : '');
            setWhapiGroupId(config.whapiGroupId || '');
        }
    }, [config]);

    const handleSave = async () => {
        const body: Record<string, unknown> = {
            whatsappEnabled: enabled,
        };
        // Only send non-empty values to avoid clearing saved config
        if (phoneNumberId) body.whatsappPhoneNumberId = phoneNumberId;
        if (businessAccountId) body.whatsappBusinessAccountId = businessAccountId;
        if (tokenEdited && accessToken) {
            body.whatsappAccessToken = accessToken;
        }
        body.whatsappReplacementNotifyPhone = replacementNotifyPhone || '';
        if (whapiTokenEdited && whapiApiToken) {
            body.whapiApiToken = whapiApiToken;
        }
        body.whapiGroupId = whapiGroupId || '';
        try {
            await updateConfig(body);
        } catch {
            toast({ title: translate('common.error'), status: 'error', duration: 3000 });
        }
    };

    if (!selectedOrganization) return null;

    return (
        <PageCard title={translate('organization.whatsapp_config_title')}>
            <Box p={4}>
                <Text mb={4} color={textColorPrimary} fontSize="sm">
                    {translate('organization.whatsapp_config_description')}
                </Text>

                {configReadState === 'loading' ? (
                    <Text color="gray.500">{translate('common.loading')}</Text>
                ) : configReadState === 'unreadable' ? (
                    <Text color="red.500">{translate('common.error')}</Text>
                ) : configReadState === 'never-issued' ? (
                    <ScopePrompt gate="organization" />
                ) : (
                    <VStack spacing={4} align="stretch">
                        <Box display="flex" alignItems="center" gap={3}>
                            <Switch
                                isChecked={enabled}
                                onChange={(e) => setEnabled(e.target.checked)}
                                colorScheme="brand"
                            />
                            <FormLabel mb={0}>{translate('organization.whatsapp_config_enable')}</FormLabel>
                        </Box>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box>
                                <FormLabel>{translate('organization.whatsapp_config_phone_number_id')}</FormLabel>
                                <Input
                                    value={phoneNumberId}
                                    onChange={(e) => setPhoneNumberId(e.target.value)}
                                    placeholder="e.g., 123456789012345"
                                />
                            </Box>
                            <Box>
                                <FormLabel>{translate('organization.whatsapp_config_business_account_id')}</FormLabel>
                                <Input
                                    value={businessAccountId}
                                    onChange={(e) => setBusinessAccountId(e.target.value)}
                                    placeholder="e.g., 123456789012345"
                                />
                            </Box>
                        </SimpleGrid>

                        <Box>
                            <FormLabel>{translate('organization.whatsapp_replacement_notify_phone')}</FormLabel>
                            <Input
                                value={replacementNotifyPhone}
                                onChange={(e) => setReplacementNotifyPhone(e.target.value)}
                                placeholder="+33612345678"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {translate('organization.whatsapp_replacement_notify_phone_hint')}
                            </Text>
                        </Box>

                        <Box>
                            <FormLabel>{translate('organization.whatsapp_config_access_token')}</FormLabel>
                            <InputGroup>
                                <Input
                                    type="password"
                                    value={tokenEdited ? accessToken : ''}
                                    placeholder={tokenPlaceholder || translate('organization.whatsapp_config_enter_token')}
                                    onChange={(e) => {
                                        setAccessToken(e.target.value);
                                        if (!tokenEdited) setTokenEdited(true);
                                    }}
                                />
                                {tokenEdited && (
                                    <InputRightElement width="4.5rem">
                                        <Button
                                            h="1.75rem"
                                            size="sm"
                                            onClick={() => {
                                                setTokenEdited(false);
                                                setAccessToken('');
                                            }}
                                        >
                                            {translate('common.cancel')}
                                        </Button>
                                    </InputRightElement>
                                )}
                            </InputGroup>
                            {config?.hasAccessToken && !tokenEdited && (
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    {translate('organization.whatsapp_config_token_configured')} ({config.accessTokenLastChars}). {translate('organization.whatsapp_config_click_to_change')}
                                </Text>
                            )}
                        </Box>

                        <Box mt={6} pt={6} borderTopWidth="1px" borderColor="gray.200">
                            <Text fontWeight="bold" mb={1} color={textColorPrimary}>
                                {translate('organization.whapi_section_title')}
                            </Text>
                            <Text mb={4} color="gray.500" fontSize="sm">
                                {translate('organization.whapi_section_description')}
                            </Text>

                            <VStack spacing={4} align="stretch">
                                <Box>
                                    <FormLabel>{translate('organization.whapi_api_token')}</FormLabel>
                                    <InputGroup>
                                        <Input
                                            type="password"
                                            value={whapiTokenEdited ? whapiApiToken : ''}
                                            placeholder={whapiTokenPlaceholder || translate('organization.whapi_api_token_enter')}
                                            onChange={(e) => {
                                                setWhapiApiToken(e.target.value);
                                                if (!whapiTokenEdited) setWhapiTokenEdited(true);
                                            }}
                                        />
                                        {whapiTokenEdited && (
                                            <InputRightElement width="4.5rem">
                                                <Button
                                                    h="1.75rem"
                                                    size="sm"
                                                    onClick={() => {
                                                        setWhapiTokenEdited(false);
                                                        setWhapiApiToken('');
                                                    }}
                                                >
                                                    {translate('common.cancel')}
                                                </Button>
                                            </InputRightElement>
                                        )}
                                    </InputGroup>
                                    {config?.hasWhapiApiToken && !whapiTokenEdited && (
                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                            {translate('organization.whapi_api_token_configured')} ({config.whapiApiTokenLastChars}). {translate('organization.whatsapp_config_click_to_change')}
                                        </Text>
                                    )}
                                </Box>

                                <Box>
                                    <FormLabel>{translate('organization.whapi_group_id')}</FormLabel>
                                    {groupsReadState === 'answered' && whapiGroups && whapiGroups.length > 0 ? (
                                        <Select
                                            value={whapiGroupId}
                                            onChange={(e) => setWhapiGroupId(e.target.value)}
                                            placeholder={translate('organization.whapi_group_id_select')}
                                        >
                                            {whapiGroups.map((group) => (
                                                <option key={group.id} value={group.id}>
                                                    {group.name} ({group.memberCount} members)
                                                </option>
                                            ))}
                                        </Select>
                                    ) : (
                                        <HStack spacing={2}>
                                            <Input
                                                value={whapiGroupId}
                                                onChange={(e) => setWhapiGroupId(e.target.value)}
                                                placeholder="120363001234567890@g.us"
                                                flex={1}
                                            />
                                            {hasWhapiToken && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => setLoadGroups(true)}
                                                    isLoading={isLoadingGroups}
                                                    isDisabled={isLoadingGroups}
                                                    whiteSpace="nowrap"
                                                >
                                                    {translate('organization.whapi_load_groups')}
                                                </Button>
                                            )}
                                        </HStack>
                                    )}
                                    {groupsReadState === 'unreadable' && (
                                        <Text fontSize="xs" color="red.500" mt={1}>
                                            {translate('organization.whapi_groups_error')}
                                        </Text>
                                    )}
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        {translate('organization.whapi_group_id_hint')}
                                    </Text>
                                </Box>
                            </VStack>
                        </Box>

                        <Box>
                            <Button
                                colorScheme="brand"
                                onClick={handleSave}
                                isLoading={isSaving}
                            >
                                {translate('common.save')}
                            </Button>
                        </Box>
                    </VStack>
                )}
            </Box>
        </PageCard>
    );
}
